import { logAuthFlowInfo, logAuthFlowWarning } from "./authFlowLogger";
import { getResponseRequestId } from "./authDiagnostics";
import { getApiBaseUrl } from "../runtime/apiBaseUrl";

export const BOOTSTRAP_REASON_CODES = Object.freeze({
    invalid: "bootstrap_invalid",
    expired: "bootstrap_expired",
    used: "bootstrap_used",
    wrongDestination: "bootstrap_wrong_destination",
    issueDenied: "bootstrap_issue_denied",
    issueFailed: "bootstrap_issue_failed",
    exchangeFailed: "bootstrap_exchange_failed",
});

function pickFirstValue(...values) {
    for (const value of values) {
        if (value === null || value === undefined) {
            continue;
        }

        if (typeof value === "string" && value.trim() === "") {
            continue;
        }

        return value;
    }

    return null;
}

function extractAccessIdentity(access) {
    const sharedAccessContext = access?.sharedAccessContext || null;
    const portalAccounts = Array.isArray(access?.portals)
        ? access.portals.map((entry) => entry?.access?.account || null).filter(Boolean)
        : [];
    const portalAccountWithEmail = portalAccounts.find((account) => account?.email);
    const portalAccountWithPhone = portalAccounts.find((account) => account?.phone || account?.authPhone);

    return {
        email: pickFirstValue(
            sharedAccessContext?.broker?.user?.email,
            sharedAccessContext?.client?.user?.email,
            sharedAccessContext?.investor?.user?.email,
            sharedAccessContext?.referrer?.user?.email,
            portalAccountWithEmail?.email,
        ),
        phone: pickFirstValue(
            sharedAccessContext?.broker?.user?.phone,
            sharedAccessContext?.client?.user?.phone,
            sharedAccessContext?.investor?.user?.phone,
            sharedAccessContext?.referrer?.user?.phone,
            sharedAccessContext?.application?.user?.authPhone,
            portalAccountWithPhone?.phone,
            portalAccountWithPhone?.authPhone,
        ),
    };
}

export async function issuePortalBootstrap({
    session,
    portal,
    requestedIntent,
    hasExplicitIntent,
    source,
    returnTo,
    rememberDevice = false,
    verifiedEmail = null,
    verifiedPhone = null,
    authUserId: explicitAuthUserId = null,
}) {
    const user = session?.user || {};
    const identity = session?.identity || {};
    const authSession = session?.session || {};
    const accessIdentity = extractAccessIdentity(session?.access);
    const phone = pickFirstValue(
        verifiedPhone,
        user?.phone,
        identity?.phone?.phoneDigits,
        identity?.phone?.phoneE164,
        accessIdentity.phone,
    );
    const email = pickFirstValue(
        verifiedEmail,
        user?.email,
        identity?.email,
        accessIdentity.email,
    );
    const authUserId = pickFirstValue(
        explicitAuthUserId,
        user?.id,
        identity?.identityId,
        authSession?.identityId,
        authSession?.sessionId,
    );

    logAuthFlowInfo("bootstrap_issue_started", {
        portalId: portal.portalId,
        requestedIntent,
        source,
        authUserId,
        phone,
        email,
    });

    const response = await fetch(`${getApiBaseUrl()}/api/auth/portal-bootstrap/issue`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
            portalId: portal.portalId,
            authUserId,
            phone,
            email,
            intent: requestedIntent,
            hasExplicitIntent,
            source,
            returnTo,
            rememberDevice,
        }),
    });

    const payload = await response.json().catch(() => ({}));
    const requestId = getResponseRequestId(response, payload);

    if (!response.ok || !payload?.bootstrap?.bootstrapCode) {
        logAuthFlowWarning("bootstrap_issue_failed", {
            portalId: portal.portalId,
            requestedIntent,
            source,
            statusCode: response.status,
            reasonCode: payload?.code || payload?.bootstrap?.code || null,
            requestId,
        });
        const error = new Error(payload?.message || "We couldn't open your workspace right now.");
        error.status = response.status;
        error.code = payload?.code || BOOTSTRAP_REASON_CODES.issueFailed;
        error.body = payload;
        error.requestId = requestId;
        throw error;
    }

    logAuthFlowInfo("bootstrap_issued", {
        portalId: portal.portalId,
        requestedIntent,
        expiresAt: payload.bootstrap.expiresAt || null,
        requestId,
    });

    return {
        ...payload.bootstrap,
        requestId,
    };
}

export function buildBootstrapDestinationUrl(destinationUrl, bootstrapCode, returnTo, portalId = null) {
    const url = new URL(destinationUrl);
    url.searchParams.set("bootstrap", bootstrapCode);

    if (portalId) {
        url.searchParams.set("intent", portalId);
    }

    if (returnTo) {
        url.searchParams.set("returnTo", returnTo);
    }

    return url.toString();
}
